import { useState, useEffect } from "react";
import { IconButton, ThemeProviderV2 } from '@blueprint/ui';
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import bankAccountApi from "../../api/bankAccount";
import { Loader } from "../../common/components/core";
import { currentRolesSelector } from "../../redux/slices/auth/selectors";
import { SuperAdminRole, superAdminAccountType } from "../../types/superadmin";

import AccountList from "./components/AccountList";
import { BankAccount, ResponseWithPagination } from "../../types/common";
import { PageTitle } from "../../types/pageTitle";
import useFilterHandler, { FilterBankAccountValues } from "./hooks/useFilterHandler";
import NoRecordCard from "../../common/components/NoRecordCard";


const AccountView = (): JSX.Element => {
    document.title = PageTitle.AccountView;
    const history = useHistory();

    const currentRoles = useSelector(currentRolesSelector);
    const accountType = superAdminAccountType(currentRoles);

    return (
        <ThemeProviderV2>
            {accountType && (
                <PureAccountView
                    accountType={accountType}
                    onAddAccount={() =>
                        history.push("/account-setup/add-bank-account")
                    }
                    onViewAccount={(accountId: number) =>
                        history.push(
                            `/account-setup/bank-account-details/${accountId}`,
                        )
                    }
                />
            )}
    </ThemeProviderV2>
    );
};

type PureAccountViewProps = {
    accountType: SuperAdminRole;
    onAddAccount: () => void;
    onViewAccount: (accountId: number) => void;
};

export const PureAccountView = ({
    accountType,
    onAddAccount,
    onViewAccount,
}: PureAccountViewProps): JSX.Element => {
    const pageSize = 10;
    
    const [profileId, setProfileId] = useState<FilterBankAccountValues>({
        userProfileId: undefined,
        pageNo: 0,
    });
    const [accountListPaging, setAccountListPaging] = useState<ResponseWithPagination<BankAccount[]>>();
    const [isAccountListLoading, setIsAccountListLoading] = useState(false);

    const { show: showFilterHandler } = useFilterHandler(
        profileId,
        (values: FilterBankAccountValues) => {
            setProfileId({userProfileId: values.userProfileId, pageNo: 0});
        },
    );
    useEffect(() => {
        const getBankAccountList = async () => {
            try {
                setIsAccountListLoading(true);
                const bankAccounts = await bankAccountApi.filterPaging(profileId.pageNo, pageSize, profileId.userProfileId);
                setAccountListPaging(bankAccounts);
                setIsAccountListLoading(false);
            } catch (e) {
                console.error(e);
                setIsAccountListLoading(false);
            }
            return;
        };
        getBankAccountList();
    }, [profileId]);


    const nextPage = () => {
        if (accountListPaging && !accountListPaging.last) {
            // setPageNo(pageNo + 1);
            setProfileId((olddata)=>{
                return {
                    ...olddata,
                    pageNo: olddata.pageNo + 1
                }
            })
        }
    };
    const prevPage = () => {
        if (profileId.pageNo > 0) {
            setProfileId((olddata)=>{
                return {
                    ...olddata,
                    pageNo: olddata.pageNo - 1
                }
            })
        }
    };
    const toPage = (page: number) => {
        if (accountListPaging && page !== accountListPaging.number) {
            if (page >= 0 && page < accountListPaging.totalPages) {
                setProfileId((olddata)=>{
                    return {
                        ...olddata,
                        pageNo: page
                    }
                })
            }
        }
    };

    return (
        <Loader isLoading={isAccountListLoading}>
            {accountListPaging && accountListPaging.content ?(
                    <div>
                    <div className="d-flex align-items-center mb-4">
                        <h1 className="flex-grow-1 mb-0">{`${accountType} Account Setup`}</h1>
                        <div>
                            <IconButton
                                icon="Filter"
                                variant="light.tertiary"
                                onClick={showFilterHandler}
                                style={{ marginRight: '20px' }}
                            >
                                Filter
                            </IconButton>
                            <IconButton icon="Add" variant="light.primary" onClick={onAddAccount}>
                                {`Add ${accountType} Bank Account`}
                            </IconButton>
                        </div>
                    </div>
                    <AccountList
                        accountType={accountType}
                        accountsPage={accountListPaging!!}
                        onClick={onViewAccount}
                        nextPage={nextPage}
                        prevPage={prevPage}
                        toPage={toPage}
                    />
                </div>
                ) : (
                    <NoRecordCard />
                )}
                
        </Loader>
    );
};

export default AccountView;
